import { lazy, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AppProvider } from './context';
import NotFound from './containers/NotFound';
import MainLayout from './layouts/MainLayout';
import BlockLoading from './components/BlockLoading';
import ModalProvider from './context/ModalProvider';
import CommonModal from './components/CommonModal';
import InstallPWA from './components/InstallPWA';
import AppInformationPopup from './components/AppInformationPopup';
import AppMaintenanceGuard from './components/AppMaintenanceGuard';
import CategoryAccessGuard from './components/CategoryAccessGuard';
import NetworkStatusBanner from './components/NetworkStatusBanner';

const Loading = () => (
	<div
		style={{
			width: '100vw',
			height: '100vh',
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
		}}
	>
		<BlockLoading />
	</div>
);

// public
const Landing = lazy(() => import('./containers/Landing'));
const Login = lazy(() => import('./containers/Login'));
const Register = lazy(() => import('./containers/Register'));
const ForgotPassword = lazy(() => import('./containers/ForgotPassword'));
const Verifikasi = lazy(() => import('./containers/Verifikasi'));

// protected
const Beranda = lazy(() => import('./containers/Beranda'));
const Leaderboard = lazy(() => import('./containers/Leaderboard'));
const RiwayatLatihan = lazy(() => import('./containers/RiwayatLatihan'));
const DetailAkun = lazy(() => import('./containers/DetailAkun'));
const Notifikasi = lazy(() => import('./containers/Notifikasi'));
const PhoneVerification = lazy(() => import('./containers/PhoneVerification'));
const SupportTickets = lazy(() => import('./containers/SupportTickets'));
const SupportTicketCreate = lazy(
	() => import('./containers/SupportTicketCreate')
);
const SupportTicketDetail = lazy(
	() => import('./containers/SupportTicketDetail')
);
const LiveClass = lazy(() => import('./containers/LiveClass'));

const CategoryMenu = lazy(() => import('./containers/CategoryMenu'));
const Kecermatan = lazy(() => import('./containers/Kecermatan'));
const KecermatanTestRunner = lazy(
	() => import('./containers/KecermatanTestRunner')
);

const InformasiTest = lazy(() => import('./containers/InformasiTest'));
const PGTestRunner = lazy(() => import('./containers/PGTestRunner'));

const Jasmani = lazy(() => import('./containers/Jasmani'));
const JasmaniTestRunner = lazy(() => import('./containers/JasmaniTestRunner'));

const TestResult = lazy(() => import('./containers/TestResult'));
const TestReview = lazy(() => import('./containers/TestReview'));

const About = lazy(() => import('./containers/About'));
const Calculator = lazy(() => import('./containers/Calculator'));
const Paket = lazy(() => import('./containers/Paket'));
const ArticleList = lazy(() => import('./containers/ArticleList'));
const ArticleDetail = lazy(() => import('./containers/ArticleDetail'));
const Affiliate = lazy(() => import('./containers/Affiliate'));

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 5 * 60 * 1000,
			gcTime: 10 * 60 * 1000,
		},
	},
});

const App = () => {
	return (
		<QueryClientProvider client={queryClient}>
			<BrowserRouter>
				<NetworkStatusBanner />
				<ModalProvider>
					<AppProvider>
						<Suspense fallback={<Loading />}>
							<AppMaintenanceGuard>
								<Routes>
									{/* Public */}
									<Route path="/" element={<Landing />} />
									<Route path="login" element={<Login />} />
									<Route path="register" element={<Register />} />
									<Route path="forgot-password" element={<ForgotPassword />} />
									<Route path="auth/action" element={<Verifikasi />} />
									<Route path="affiliate" element={<Affiliate />} />

									{/* Protected under MainLayout */}
									<Route element={<MainLayout />}>
										{/* Beranda & sub-pages */}
										<Route path="beranda">
											<Route index element={<Beranda />} />
											<Route
												path="riwayat-latihan"
												element={<RiwayatLatihan />}
											/>
											<Route path="detail-akun" element={<DetailAkun />} />
											<Route
												path="verifikasi-nomor-hp"
												element={<PhoneVerification />}
											/>
											<Route path="notifikasi" element={<Notifikasi />} />
											<Route path="live-class" element={<LiveClass />} />
											<Route
												path="live-class/:sessionId"
												element={<LiveClass />}
											/>
											<Route
												path="tiket-bantuan"
												element={<SupportTickets />}
											/>
											<Route
												path="tiket-bantuan/buat"
												element={<SupportTicketCreate />}
											/>
											<Route
												path="tiket-bantuan/:ticketId"
												element={<SupportTicketDetail />}
											/>
										</Route>

										<Route path="leaderboard" element={<Leaderboard />} />

										{/* Psikologi */}
										<Route
											element={<CategoryAccessGuard category="psikologi" />}
										>
											<Route path="psikologi">
												<Route
													index
													element={<CategoryMenu category="psikologi" />}
												/>

												{/* pilihan sub–sub–kategori */}
												<Route path="kecermatan" element={<Kecermatan />} />

												{/* setelah user pilih: */}
												<Route
													path="kecermatan/:subkategori/informasi"
													element={<InformasiTest />}
												/>
												<Route
													path="kecermatan/:subkategori/kerjakan-test"
													element={<KecermatanTestRunner />}
												/>

												<Route
													path="kecermatan/:subkategori/result"
													element={<TestResult />}
												/>

												{/* generic pilihan ganda lainnya */}
												<Route
													path=":kategori/informasi"
													element={<InformasiTest />}
												/>
												<Route
													path=":kategori/kerjakan-test"
													element={<PGTestRunner type="psikologi" />}
												/>
												<Route
													path=":kategori/result"
													element={<TestResult type="psikologi" />}
												/>
												<Route
													path=":kategori/pembahasan"
													element={<TestReview />}
												/>
											</Route>
										</Route>

										{/* Akademik */}
										<Route
											element={<CategoryAccessGuard category="akademik" />}
										>
											<Route path="akademik">
												<Route
													index
													element={<CategoryMenu category="akademik" />}
												/>
												<Route path=":kategori">
													<Route path="informasi" element={<InformasiTest />} />
													<Route
														path="kerjakan-test"
														element={<PGTestRunner type="akademik" />}
													/>
													<Route
														path="result"
														element={<TestResult type="akademik" />}
													/>
													<Route path="pembahasan" element={<TestReview />} />
												</Route>
											</Route>
										</Route>

										{/* Jasmani */}
										<Route element={<CategoryAccessGuard category="jasmani" />}>
											<Route path="jasmani">
												<Route index element={<Jasmani />} />
												<Route path=":kategori">
													<Route path="informasi" element={<InformasiTest />} />
													<Route
														path="mulai-test"
														element={<JasmaniTestRunner />}
													/>
												</Route>
											</Route>
										</Route>

										<Route path="about" element={<About />} />
									</Route>

									{/* Standalone protected — layout sendiri */}
									<Route path="kalkulator" element={<Calculator />} />
									<Route path="paket" element={<Paket />} />

									{/* Blog / Article — public, standalone layout */}
									<Route path="blog" element={<ArticleList />} />
									<Route path="blog/:slug" element={<ArticleDetail />} />

									{/* Catch-all */}
									<Route path="*" element={<NotFound />} />
								</Routes>
								<AppInformationPopup />
							</AppMaintenanceGuard>
						</Suspense>
					</AppProvider>
					<CommonModal />
					<InstallPWA />
				</ModalProvider>
			</BrowserRouter>
		</QueryClientProvider>
	);
};

export default App;
